import { useQuery } from '@tanstack/react-query'
import { Eye, EyeOff, LogOut, Mic } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { cn } from '../lib/cn'
import { get, post } from '../lib/api'
import { clearStaffSession, getStaffProfile, getStaffSession } from '../lib/staff'

/**
 * The compère's console — the name, early and in one size.
 *
 * Built for somebody holding a microphone, not driving an event. The stage
 * screen shows a winner at the same instant as the hall, from an angle, in a
 * font meant for the back of the room — which is why names were being read
 * wrong. This shows the same name **before** the projector does, on a phone
 * at arm's length.
 *
 * Three rules shaped it:
 *
 *   - **One thing, enormous.** Whatever is about to be said fills the screen.
 *     A dashboard with six panels is unreadable at a lectern.
 *   - **It says whether the hall knows yet.** A name the audience has not
 *     seen reads differently from one already on the projector, so the badge
 *     at the top is the first thing on the page.
 *   - **No buttons that do anything.** There is nothing here to press by
 *     accident mid-sentence; the only control is signing out.
 */

interface McName {
  name: string
  detail: string | null
  regNo: string | null
}

interface McState {
  screen: { mode: string; drawNo: number | null; awardCode: string | null }
  stage: { busy: boolean; what: 'draw' | 'reveal' | null; msRemaining: number }
  draw: {
    drawNo: number
    name: string
    announced: boolean
    position: number
    winner: McName | null
  } | null
  award: {
    code: string
    title: string
    place: number
    revealed: boolean
    winner: McName | null
  } | null
}

const PLACE = ['', '1st place', '2nd place', '3rd place'] as const

export default function McConsole() {
  const navigate = useNavigate()
  const signedIn = !!getStaffSession()
  const profile = getStaffProfile()

  useEffect(() => {
    if (!signedIn) navigate('/staff/login', { replace: true })
  }, [signedIn, navigate])

  /*
   * Polled once a second, not streamed. The console shows one line of text
   * and a second of lag costs nothing — where an SSE connection dropped
   * behind a lectern, with nobody watching for it, would cost exactly the
   * mistake this page exists to prevent.
   */
  const state = useQuery({
    queryKey: ['mc-state'],
    queryFn: () => get<McState>('/api/mc/state', 'staff'),
    enabled: signedIn,
    refetchInterval: 1000,
    refetchIntervalInBackground: true,
  })

  const s = state.data

  /* Whichever is live. An award mid-reveal outranks a draw sitting finished. */
  const showAward = s?.award != null && (s.screen.mode === 'award' || s.draw == null)
  const subject = showAward ? s?.award?.winner : s?.draw?.winner
  const announced = showAward ? (s?.award?.revealed ?? false) : (s?.draw?.announced ?? false)

  const heading = showAward
    ? `${s?.award?.title ?? ''}${s?.award?.place && s.award.place <= 3 ? ` · ${PLACE[s.award.place]}` : ''}`
    : s?.draw
      ? `${s.draw.name} · Prize ${s.draw.position}`
      : ''

  async function signOut() {
    await post('/api/staff/logout', {}, 'staff').catch(() => {})
    clearStaffSession()
    navigate('/staff/login', { replace: true })
  }

  return (
    <div className="flex min-h-dvh flex-col bg-[#0B1225] text-white">
      <header className="flex shrink-0 items-center justify-between gap-3 px-5 py-3">
        <span className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.18em] text-white/50">
          <Mic className="size-4" strokeWidth={2.2} />
          MC console
        </span>
        <span className="flex items-center gap-3">
          <span className="text-xs text-white/40">{profile?.name ?? profile?.username}</span>
          <Button
            variant="link"
            onClick={signOut}
            className="h-auto p-0 text-xs font-semibold text-white/40 hover:text-white/80"
          >
            <LogOut className="size-3.5" strokeWidth={2.2} />
            Sign out
          </Button>
        </span>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-5 pb-10 text-center">
        {!subject ? (
          <>
            <p className="text-[clamp(1.1rem,4vw,1.8rem)] font-bold uppercase tracking-[0.25em] text-white/25">
              Nothing to announce
            </p>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-white/35">
              The next name appears here the moment the Committee picks it — before it reaches
              the big screen.
            </p>
          </>
        ) : (
          <>
            {/*
              Whether the hall knows. First on the page and colour-coded,
              because reading a name out that is already on the projector and
              reading one that is not are different jobs.
            */}
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[clamp(0.7rem,2.2vw,0.9rem)] font-extrabold uppercase tracking-[0.18em]',
                announced
                  ? 'bg-emerald-500/15 text-emerald-300'
                  : 'bg-amber-400/15 text-amber-300',
              )}
            >
              {announced ? (
                <>
                  <Eye className="size-4" strokeWidth={2.4} />
                  On screen now
                </>
              ) : (
                <>
                  <EyeOff className="size-4" strokeWidth={2.4} />
                  Not on screen yet
                </>
              )}
            </span>

            <p className="mt-5 text-[clamp(0.8rem,2.6vw,1.1rem)] font-semibold uppercase tracking-[0.2em] text-white/45">
              {heading}
            </p>

            {/*
              The name, as large as it will go. `clamp` rather than a fixed
              size so a long Malaysian or Nepali name still fits a phone held
              at arm's length without wrapping into four lines.
            */}
            <h1 className="mt-3 max-w-[22ch] text-balance text-[clamp(2.2rem,11vw,6rem)] font-extrabold leading-[1.05] tracking-tight">
              {subject.name}
            </h1>

            {subject.detail && (
              <p className="mt-4 max-w-[30ch] text-[clamp(0.95rem,3.4vw,1.5rem)] font-medium leading-snug text-white/55">
                {subject.detail}
              </p>
            )}

            {subject.regNo && (
              <p className="mt-3 font-mono text-[clamp(0.85rem,2.8vw,1.2rem)] tracking-[0.2em] text-white/35">
                {subject.regNo}
              </p>
            )}

            {s?.stage.busy && (
              <p className="mt-6 text-sm font-semibold text-white/40">
                Screen is announcing · {Math.ceil(s.stage.msRemaining / 1000)}s
              </p>
            )}
          </>
        )}
      </main>

      {state.isError && (
        <p className="shrink-0 pb-4 text-center text-xs text-red-300/70">
          Lost contact with the server — retrying.
        </p>
      )}
    </div>
  )
}
